import React, { useState, useEffect } from 'react';
import {
  Box,
  Typography,
  Button,
  Card,
  CardContent,
  Modal,
  Grid,
  Chip,
  Divider,
  CircularProgress,
  Alert,
  IconButton
} from '@mui/material';
import {
  MdClose,
  MdCheckCircle,
  MdQrCode,
  MdPix,
  MdContentCopy,
  MdArrowBack
} from 'react-icons/md';
import { toast } from 'react-toastify';
import QRCode from 'react-qr-code';
// Removido imports das constantes antigas de planos
import * as S from './styles';

interface PlansModalProps {
  open: boolean;
  onClose: () => void;
  onSuccess?: (planId?: string) => void;
  // Receber props para todo controle de pix state.
  loading: boolean;
  paymentStatus: string;
  currentTransaction: string | null;
  qrCode: string | null;
  qrCodeText: string | null;
  createPixPayment: (amount: number, description: string) => Promise<string>;
  resetPayment: () => void;
  checkPaymentStatus: (transactionId: string) => void;
  selectedPlan: PlanOption | null;
  setSelectedPlan: (plan: PlanOption | null) => void;
  paymentMethod: 'pix';
  setPaymentMethod: (method: 'pix') => void;
  showPixPayment: boolean;
  setShowPixPayment: (show: boolean) => void;
  handlePixPayment: () => void;
}

interface PlanOption {
  id: 'trial' | 'premium';
  name: string;
  price: string;
  originalPrice?: string;
  discount?: string;
  description: string;
  period: string;
  isPopular?: boolean;
  isTrial?: boolean;
}

const planOptions: PlanOption[] = [
  {
    id: 'trial',
    name: 'Teste',
    price: 'R$ 1,00',
    description: 'Teste por 7 dias',
    period: 'teste',
    isTrial: true
  },
  {
    id: 'premium',
    name: 'Premium',
    price: 'R$ 49,90',
    description: 'Acesso completo a todas as funcionalidades',
    period: 'mês',
    isPopular: true
  }
];

export const PlansModal: React.FC<PlansModalProps> = ({
  open,
  onClose,
  onSuccess,
  loading,
  paymentStatus,
  currentTransaction,
  qrCode,
  qrCodeText,
  createPixPayment,
  resetPayment,
  checkPaymentStatus,
  selectedPlan,
  setSelectedPlan,
  paymentMethod,
  setPaymentMethod,
  showPixPayment,
  setShowPixPayment,
  handlePixPayment
}) => {
  // Reset do estado quando o modal fecha
  useEffect(() => {
    if (!open) {
      setSelectedPlan(null);
      setPaymentMethod('pix');
      resetPayment();
    }
  }, [open, resetPayment, setSelectedPlan, setPaymentMethod]);

  // Verificar se o pagamento foi concluído
  useEffect(() => {
    if (paymentStatus === 'completed') {
      toast.success('Pagamento confirmado! Bem-vindo ao Precision!');
      onSuccess?.(selectedPlan?.id);
      setTimeout(() => {
        onClose();
      }, 2000);
    }
  }, [paymentStatus, onSuccess, onClose, selectedPlan]);

  const handlePlanSelect = (plan: PlanOption) => {
    setSelectedPlan(plan);
  };


  const handleClosePixPayment = () => {
    // setShowPixPayment(false); // Removido
    resetPayment();
  };

  const handleRefreshPayment = async () => {
    if (selectedPlan) {
      await createPixPayment(selectedPlan.id === 'trial' ? 1 : 49.90, `Assinatura ${selectedPlan.name} - Precision`);
    }
  };

  const handleCopyPixCode = async () => {
    if (!qrCodeText) return;
    
    try {
      await navigator.clipboard.writeText(qrCodeText);
      toast.success('Código PIX copiado!');
    } catch (error) {
      toast.error('Erro ao copiar código PIX');
    }
  };

  const formatCurrency = (value: number) => {
    return new Intl.NumberFormat('pt-BR', {
      style: 'currency',
      currency: 'BRL'
    }).format(value);
  };

  const renderTimeline = () => {
    const steps = [
      {
        number: 1,
        label: 'Escolher',
        description: 'Plano',
        completed: !!selectedPlan,
        active: !selectedPlan
      },
      {
        number: 2,
        label: 'Pagar',
        description: 'PIX',
        completed: paymentStatus === 'completed',
        active: !!selectedPlan && paymentStatus !== 'completed'
      },
      {
        number: 3,
        label: 'Confirmar',
        description: 'Pagamento',
        completed: paymentStatus === 'completed',
        active: paymentStatus !== 'completed'
      },
      {
        number: 4,
        label: 'Acessar',
        description: 'Precision',
        completed: paymentStatus === 'completed',
        active: paymentStatus === 'completed'
      }
    ];

    return (
      <S.TimelineContainer>
        <S.TimelineTitle>
          Passos para sua Assinatura
        </S.TimelineTitle>
        <S.TimelineSteps>
          {steps.map((step) => (
            <S.StepWrapper key={step.number}>
              <S.TimelineStep 
                active={!!step.active} 
                completed={!!step.completed}
              >
                {step.completed ? (
                  <MdCheckCircle size={16} color="#0d4f3c" />
                ) : (
                  <S.StepNumber completed={!!step.completed} active={!!step.active}>
                    {step.number}
                  </S.StepNumber>
                )}
              </S.TimelineStep>
              <S.StepLabel active={!!step.active} completed={!!step.completed}>
                {step.label}
              </S.StepLabel>
              <S.StepDescription active={!!step.active} completed={!!step.completed}>
                {step.description}
              </S.StepDescription>
            </S.StepWrapper>
          ))}
        </S.TimelineSteps>
      </S.TimelineContainer>
    );
  };

  const getStatusColor = () => {
    switch (paymentStatus) {
      case 'completed':
        return 'success';
      case 'failed':
        return 'error';
      default:
        return 'info';
    }
  };

  const getStatusMessage = () => {
    switch (paymentStatus) {
      case 'completed':
        return 'Pagamento confirmado com sucesso!';
      case 'failed':
        return 'Pagamento falhou. Tente novamente.';
      default:
        return 'Aguardando pagamento...';
    }
  };

  const renderPlanCard = (plan: PlanOption) => (
    <Card
      key={plan.id}
      sx={{
        height: '100%',
        cursor: 'pointer',
        border: selectedPlan?.id === plan.id ? '2px solid #49fbb0' : '1px solid rgba(73, 251, 176, 0.3)',
        background: selectedPlan?.id === plan.id 
          ? plan.id === 'premium' 
            ? 'linear-gradient(135deg, rgba(0, 0, 0, 0.9) 0%, rgba(0, 0, 0, 0.95) 100%)'
            : 'linear-gradient(135deg, rgba(73, 251, 176, 0.2) 0%, rgba(13, 79, 60, 0.3) 100%)'
          : plan.id === 'premium'
            ? 'linear-gradient(135deg, rgba(0, 0, 0, 0.8) 0%, rgba(0, 0, 0, 0.9) 100%)'
            : 'linear-gradient(135deg, rgba(73, 251, 176, 0.1) 0%, rgba(13, 79, 60, 0.2) 100%)',
        backdropFilter: 'blur(10px)',
        transition: 'all 0.4s cubic-bezier(0.4, 0, 0.2, 1)',
        borderRadius: '16px',
        position: 'relative',
        overflow: 'hidden',
        boxShadow: selectedPlan?.id === plan.id 
          ? '0 0 30px rgba(73, 251, 176, 0.4), 0 8px 32px rgba(0, 0, 0, 0.3)'
          : plan.id === 'premium' 
            ? '0 0 20px rgba(73, 251, 176, 0.3), 0 6px 25px rgba(0, 0, 0, 0.25), inset 0 1px 0 rgba(255, 255, 255, 0.1)'
            : '0 4px 20px rgba(0, 0, 0, 0.2), 0 0 10px rgba(73, 251, 176, 0.1)',
        '&:hover': {
          transform: 'translateY(-8px) scale(1.02)',
          boxShadow: '0 0 40px rgba(73, 251, 176, 0.6), 0 12px 40px rgba(0, 0, 0, 0.4)',
          borderColor: '#49fbb0',
          background: plan.id === 'premium' 
            ? 'linear-gradient(135deg, rgba(0, 0, 0, 0.95) 0%, rgba(0, 0, 0, 1) 100%)'
            : 'linear-gradient(135deg, rgba(73, 251, 176, 0.15) 0%, rgba(13, 79, 60, 0.25) 100%)',
        },
        '&::before': {
          content: '""',
          position: 'absolute',
          top: 0,
          left: '-100%',
          width: '100%',
          height: '100%',
          background: 'linear-gradient(90deg, transparent, rgba(73, 251, 176, 0.1), transparent)',
          transition: 'left 0.6s ease',
        },
        '&:hover::before': {
          left: '100%',
        },
        // Otimização para mobile
        '@media (max-width: 768px)': {
          '&:hover': {
            transform: 'translateY(-4px) scale(1.01)',
            boxShadow: '0 0 25px rgba(73, 251, 176, 0.4), 0 8px 25px rgba(0, 0, 0, 0.3)',
          }
        },
        '@media (max-width: 480px)': {
          '&:hover': {
            transform: 'translateY(-2px)',
            boxShadow: '0 0 15px rgba(73, 251, 176, 0.3), 0 4px 15px rgba(0, 0, 0, 0.2)',
          },
          '&::before': {
            display: 'none', // Remove efeito pesado em telas muito pequenas
          }
        }
      }}
      onClick={() => handlePlanSelect(plan)}
    >
      <CardContent sx={{ p: 3, height: '100%', display: 'flex', flexDirection: 'column' }}>
        {/* Header do plano */}
        <Box sx={{ mb: 2 }}>
          <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', mb: 1 }}>
            {plan.id === 'premium' ? (
              <S.RPGText variant="title">
                <Typography variant="h4" sx={{ fontWeight: 'bold', color: '#49fbb0', fontSize: { xs: '1.5rem', sm: '1.8rem', md: '2rem' } }}>
                  {plan.name}
                </Typography>
              </S.RPGText>
            ) : (
              <Typography variant="h4" sx={{ fontWeight: 'bold', color: '#49fbb0', fontSize: { xs: '1.5rem', sm: '1.8rem', md: '2rem' } }}>
                {plan.name}
              </Typography>
            )}
            
            {plan.isPopular && (
              <Chip
                label="Popular"
                size="small"
                sx={{
                  backgroundColor: '#49fbb0',
                  color: '#183530',
                  fontWeight: 'bold',
                  fontSize: '0.8rem',
                  height: '28px'
                }}
              />
            )}
            
            {plan.isTrial && (
              <Chip
                label="Teste"
                size="small"
                color="secondary"
                sx={{ fontWeight: 'bold', fontSize: '0.8rem', height: '28px' }}
              />
            )}
          </Box>
          
          <Typography variant="h6" color="text.secondary" sx={{ mb: 2, fontSize: { xs: '1rem', sm: '1.1rem', md: '1.2rem' } }}>
            {plan.description}
          </Typography>
        </Box>

        {/* Preços */}
        <Box sx={{ mb: 2 }}>
          {plan.originalPrice && (
            <Typography 
              variant="body2" 
              sx={{ 
                textDecoration: 'line-through', 
                color: 'text.secondary',
                mb: 0.5
              }}
            >
              De {plan.originalPrice}
            </Typography>
          )}
          
          {plan.id === 'premium' ? (
            <S.RPGText variant="price">
              <Typography variant="h3" sx={{ fontWeight: 'bold', color: '#49fbb0', mb: 1, fontSize: { xs: '1.8rem', sm: '2.2rem', md: '2.5rem' } }}>
                por {plan.price}
              </Typography>
            </S.RPGText>
          ) : (
            <Typography variant="h3" sx={{ fontWeight: 'bold', color: '#49fbb0', mb: 1, fontSize: { xs: '1.8rem', sm: '2.2rem', md: '2.5rem' } }}>
              por {plan.price}
            </Typography>
          )}
          
          {plan.discount && (
            <Chip
              label={plan.discount}
              size="small"
              color="success"
              variant="outlined"
              sx={{ fontWeight: 'bold' }}
            />
          )}
        </Box>

        {/* Funcionalidades do Premium */}
        {plan.id === 'premium' && (
          <Box sx={{ mb: 2 }}>
            <S.RPGText variant="title">
              <Typography variant="h5" sx={{ fontWeight: 'bold', color: '#49fbb0', mb: 1.5, fontSize: { xs: '1.3rem', sm: '1.5rem', md: '1.6rem' } }}>
                Inclui:
              </Typography>
            </S.RPGText>
            <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1 }}>
              <S.RPGText variant="feature">
                <Typography variant="h6" color="text.secondary" sx={{ fontSize: { xs: '1.1rem', sm: '1.2rem', md: '1.25rem' } }}>
                  ✓ Todas as ligas
                </Typography>
              </S.RPGText>
              <S.RPGText variant="feature">
                <Typography variant="h6" color="text.secondary" sx={{ fontSize: { xs: '1.1rem', sm: '1.2rem', md: '1.25rem' } }}>
                  ✓ Jogos ao vivo
                </Typography>
              </S.RPGText>
              <S.RPGText variant="feature">
                <Typography variant="h6" color="text.secondary" sx={{ fontSize: { xs: '1.1rem', sm: '1.2rem', md: '1.25rem' } }}>
                  ✓ Placar ao vivo
                </Typography>
              </S.RPGText>
              <S.RPGText variant="feature">
                <Typography variant="h6" color="text.secondary" sx={{ fontSize: { xs: '1.1rem', sm: '1.2rem', md: '1.25rem' } }}>
                  ✓ Gráfico de pressão em tempo real
                </Typography>
              </S.RPGText>
              <S.RPGText variant="feature">
                <Typography variant="h6" color="text.secondary" sx={{ fontSize: { xs: '1.1rem', sm: '1.2rem', md: '1.25rem' } }}>
                  ✓ Gráfico de posse de bola em tempo real
                </Typography>
              </S.RPGText>
              <S.RPGText variant="feature">
                <Typography variant="h6" color="text.secondary" sx={{ fontSize: { xs: '1.1rem', sm: '1.2rem', md: '1.25rem' } }}>
                  ✓ Predições de gols +/- por IA
                </Typography>
              </S.RPGText>
              <S.RPGText variant="feature">
                <Typography variant="h6" color="text.secondary" sx={{ fontSize: { xs: '1.1rem', sm: '1.2rem', md: '1.25rem' } }}>
                  ✓ Predição de placar por IA
                </Typography>
              </S.RPGText>
              <S.RPGText variant="feature">
                <Typography variant="h6" color="text.secondary" sx={{ fontSize: { xs: '1.1rem', sm: '1.2rem', md: '1.25rem' } }}>
                  ✓ Predição de vencedor por Super IA
                </Typography>
              </S.RPGText>
              <S.RPGText variant="feature">
                <Typography variant="h6" color="text.secondary" sx={{ fontSize: { xs: '1.1rem', sm: '1.2rem', md: '1.25rem' } }}>
                  ✓ Brasileirão completo
                </Typography>
              </S.RPGText>
              <S.RPGText variant="feature">
                <Typography variant="h6" color="text.secondary" sx={{ fontSize: { xs: '1.1rem', sm: '1.2rem', md: '1.25rem' } }}>
                  ✓ Bundesliga completa
                </Typography>
              </S.RPGText>
              <S.RPGText variant="feature">
                <Typography variant="h6" color="text.secondary" sx={{ fontSize: { xs: '1.1rem', sm: '1.2rem', md: '1.25rem' } }}>
                  ✓ Todas as ligas do mundo
                </Typography>
              </S.RPGText>
              <S.RPGText variant="feature">
                <Typography variant="h6" color="text.secondary" sx={{ fontSize: { xs: '1.1rem', sm: '1.2rem', md: '1.25rem' } }}>
                  ✓ Todos os jogos do mundo
                </Typography>
              </S.RPGText>
            </Box>
          </Box>
        )}

        {/* Período */}
        <Box sx={{ mt: 'auto' }}>
          <Typography variant="caption" color="text.secondary">
            Período: {plan.period}
          </Typography>
        </Box>
      </CardContent>
    </Card>
  );

  const renderPaymentSection = () => {
    if (!selectedPlan) return null;

    return (
      <Box sx={{ mt: 4 }}>
        <Divider sx={{ mb: 3, borderColor: 'rgba(73, 251, 176, 0.2)' }} />
        
        <Typography variant="h6" sx={{ mb: 2, fontWeight: 'bold', color: '#49fbb0' }}>
          Pagamento via PIX
        </Typography>

        {/* Botão de pagamento */}
        <Button
          fullWidth
          color="primary"
          variant="contained"
          size="large"
          disabled={!selectedPlan}
          sx={{
            background: 'linear-gradient(135deg, #49fbb0 0%, #36d399 100%)',
            color: '#183530',
            fontWeight: 600,
            py: 1.5,
            fontSize: '1.1rem',
            '&:hover': {
              background: 'linear-gradient(135deg, #36d399 0%, #2dd4aa 100%)',
              transform: 'translateY(-1px)',
              boxShadow: '0 4px 12px rgba(73, 251, 176, 0.3)'
            },
            '&:disabled': {
              background: 'rgba(73, 251, 176, 0.3)',
              color: 'rgba(24, 53, 48, 0.5)'
            }
          }}
          onClick={() =>
            selectedPlan && createPixPayment(
              selectedPlan.id === 'trial' ? 1 : 49.90,
              `Assinatura ${selectedPlan.name} - Precision`
            )
          }
        >
          {loading ? (
            <CircularProgress size={24} sx={{ color: '#183530' }} />
          ) : (
            `Pagar ${selectedPlan.id === 'trial' ? 'R$ 1,00' : 'R$ 49,90'}`
          )}
        </Button>

        {/* Informações adicionais */}
        <Alert 
          severity="info" 
          sx={{ 
            mt: 2,
            backgroundColor: 'rgba(73, 251, 176, 0.1)',
            border: '1px solid rgba(73, 251, 176, 0.3)',
            '& .MuiAlert-icon': {
              color: '#49fbb0'
            }
          }}
        >
          <Typography variant="body2" sx={{ color: '#49fbb0' }}>
            <strong>PIX:</strong> Pagamento instantâneo e sem taxas
          </Typography>
        </Alert>
      </Box>
    );
  };

  const renderPixPaymentContent = () => {
    if (!selectedPlan || !qrCodeText) return null;

    return (
      <Box sx={{ mt: 4 }}>
        {/* Ao invés do botão voltar, mostrar apenas o passo, e só mostrar botão finalizar quando completed */}
        <Divider sx={{ mb: 3 }} />

        {/* Informações do pagamento */}
        <Card sx={{ mb: 3, background: 'rgba(73, 251, 176, 0.05)' }}>
          <CardContent>
            <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
              <Typography variant="h5" sx={{ fontWeight: 'bold', color: '#49fbb0' }}>
                {formatCurrency(selectedPlan.id === 'trial' ? 1 : 49.90)}
              </Typography>
              <Chip
                label={getStatusMessage()}
                color={getStatusColor() as any}
                variant="outlined"
                icon={paymentStatus === 'completed' ? <MdCheckCircle /> : undefined}
              />
            </Box>
            <Typography variant="body2" color="text.secondary">
              {`Assinatura ${selectedPlan.name} - Precision`}
            </Typography>
          </CardContent>
        </Card>

        {/* QR Code */}
        <Card sx={{ mb: 3 }}>
          <CardContent sx={{ textAlign: 'center' }}>
            <Typography variant="h6" sx={{ mb: 2, fontWeight: 'bold' }}>
              Escaneie o QR Code
            </Typography>
            <Box sx={{ 
              display: 'flex', 
              justifyContent: 'center', 
              alignItems: 'center',
              minHeight: '300px',
              background: 'white',
              borderRadius: 2,
              p: 2
            }}>
              {loading ? (
                <CircularProgress size={60} />
              ) : (
                <QRCode
                  value={qrCodeText}
                  size={256}
                  style={{ height: 'auto', maxWidth: '100%', width: '100%' }}
                />
              )}
            </Box>
          </CardContent>
        </Card>

        {/* Código PIX para copiar */}
        <Card sx={{ mb: 3 }}>
          <CardContent>
            <Typography variant="h6" sx={{ mb: 2, fontWeight: 'bold' }}>
              Ou copie o código PIX
            </Typography>
            <Box sx={{ 
              display: 'flex', 
              alignItems: 'center', 
              gap: 1,
              p: 2,
              background: 'rgba(0, 0, 0, 0.05)',
              borderRadius: 1,
              border: '1px solid rgba(0, 0, 0, 0.1)'
            }}>
              <Typography 
                variant="body2" 
                sx={{ 
                  flex: 1, 
                  wordBreak: 'break-all',
                  fontFamily: 'monospace',
                  fontSize: '0.875rem'
                }}
              >
                {qrCodeText}
              </Typography>
              <IconButton
                onClick={handleCopyPixCode}
                color="primary"
                size="small"
              >
                <MdContentCopy />
              </IconButton>
            </Box>
          </CardContent>
        </Card>

        {/* Instruções */}
        <Alert severity="info" sx={{ mb: 3 }}>
          <Typography variant="body2">
            <strong>Como pagar:</strong><br/>
            1. Abra o app do seu banco<br/>
            2. Escaneie o QR Code ou cole o código PIX<br/>
            3. Confirme o pagamento<br/>
            4. Aguarde a confirmação automática 🕓
          </Typography>
        </Alert>

        {/* Só mostrar botão finalizar se status for completed */}
        <Box sx={{ display: 'flex', gap: 2, justifyContent: 'center' }}>
          {paymentStatus === 'completed' && (
            <Button
              variant="contained"
              onClick={onClose}
              sx={{
                background: 'linear-gradient(135deg, #49fbb0 0%, #36d399 100%)',
                color: '#183530',
                fontWeight: 600,
                '&:hover': {
                  background: 'linear-gradient(135deg, #36d399 0%, #2dd4aa 100%)'
                }
              }}
            >
              Finalizar
            </Button>
          )}
        </Box>

        {/* Status de verificação automática */}
        {paymentStatus === 'pending' && (
          <Box sx={{ mt: 2, textAlign: 'center' }}>
            <Typography variant="caption" color="text.secondary">
              Verificando pagamento automaticamente a cada 30 segundos...
            </Typography>
          </Box>
        )}
      </Box>
    );
  };

  // Atualizar renderização para basear sempre em showPixPayment calculado
  return (
    <>
      <Modal
        open={open}
        onClose={onClose}
        sx={{
          zIndex: 10001,
          '& .MuiBackdrop-root': {
            backgroundColor: 'rgba(0, 0, 0, 0.8)',
            backdropFilter: 'blur(5px)'
          }
        }}
      >
        <S.ModalContainer>
          <S.ModalContent>
            {/* Header */}
            <S.ModalHeader>
              <Typography variant="h4" sx={{ fontWeight: 'bold', color: '#49fbb0' }}>
                Nossos Planos
              </Typography>
              <Typography variant="body1" color="text.secondary">
                Escolha o seu plano ideal
              </Typography>
              <IconButton
                onClick={onClose}
                sx={{
                  position: 'absolute',
                  top: 20,
                  right: 20,
                  color: 'text.secondary',
                  '&:hover': {
                    color: '#49fbb0'
                  }
                }}
              >
                <MdClose size={24} />
              </IconButton>
            </S.ModalHeader>

            {/* Timeline */}
            {renderTimeline()}

            {/* Conteúdo */}
            <S.ModalBody>
              {paymentStatus === 'completed' ? (
                // Conteúdo do QR Code
                renderPixPaymentContent()
              ) : (
                <>
                  {/* Grid de planos */}
                  <Grid container spacing={4} sx={{ mb: 4, justifyContent: 'center' }}>
                    {planOptions.map((plan) => (
                      <Grid item xs={12} sm={6} md={5} key={plan.id}>
                        {plan.id === 'premium' ? (
                          <S.PremiumCardContainer>
                            {renderPlanCard(plan)}
                          </S.PremiumCardContainer>
                        ) : (
                          <S.PlanCardContainer>
                            {renderPlanCard(plan)}
                          </S.PlanCardContainer>
                        )}
                      </Grid>
                    ))}
                  </Grid>
                  {renderPaymentSection()}
                </>
              )}
              <br />
            </S.ModalBody>
          </S.ModalContent>
        </S.ModalContainer>
      </Modal>
    </>
  );
};
